/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Component, effect, input, InputSignal, output, OutputEmitterRef, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  createEmptyStateResource,
  EMPTY_STRING,
  fieldEmptyValidator,
  hasStateResourceError,
  StateResource,
} from '@common/tech-shared';
import { ButtonWithSpinnerComponent, TextEditorComponent } from '@odc/component';
import { DialogComponent, DialogContentComponent, DialogFooterComponent, DialogHeaderComponent } from '@ods/system';
import { isNil } from 'lodash-es';

export const DEVICE_LABEL_FIELD: string = 'label';

@Component({
  selector: 'administration-rename-device-dialog',
  imports: [
    ButtonWithSpinnerComponent,
    DialogComponent,
    DialogContentComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ReactiveFormsModule,
    TextEditorComponent,
  ],
  templateUrl: './rename-device-dialog.component.html',
})
export class RenameDeviceDialogComponent {
  public readonly deviceLabel: InputSignal<string> = input.required<string>();
  public readonly stateResource: InputSignal<StateResource<string>> =
    input<StateResource<string>>(createEmptyStateResource<string>());

  public readonly renamed: OutputEmitterRef<string> = output<string>();

  @ViewChild(DialogComponent) private readonly dialog!: DialogComponent;

  public readonly labelControl: FormControl = new FormControl(EMPTY_STRING, fieldEmptyValidator(DEVICE_LABEL_FIELD));

  constructor() {
    effect((): void => this._closeOnSuccess(this.stateResource()));
  }

  _closeOnSuccess(stateResource: StateResource<string>): void {
    if (!isNil(stateResource) && stateResource.loaded && !hasStateResourceError(stateResource)) this.close();
  }

  public open(): void {
    this.labelControl.setValue(this.deviceLabel());
    this.dialog.open();
  }

  public close(): void {
    this.dialog.close();
  }

  public save(): void {
    if (this.labelControl.invalid) {
      this.showValidationError();
      return;
    }
    this.renamed.emit(this.labelControl.value);
  }

  private showValidationError(): void {
    this.labelControl.updateValueAndValidity();
  }
}
